/**
 * PasswordResetRoute — /nytt-passord/:token (#141).
 *
 * Lenken lages av en admin (POST /api/members/:id/password-reset) og er
 * engangs. Flyten:
 *   1. GET  /api/auth/password-reset/:token        → PasswordResetPreview
 *   2. POST /api/auth/password-reset/:token/accept → { ok: true }
 *   3. «Passordet er lagret» + lenke til /login (accept logger ikke inn)
 *
 * Ugyldig, brukt eller utløpt lenke (4xx på preview, eller
 * `invalid_or_expired_reset` på accept) gir samme forklaring. Uautentisert
 * rute, som /bli-med/:token.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { PasswordResetPreview } from '@stell/shared';
import { Button } from '../../components/primitives';
import { AuthApi, asApiError, authErrorMessage } from '../auth/authApi';
import {
  EMPTY_NEW_PASSWORD,
  HiddenUsername,
  NewPasswordFields,
  isNewPasswordValid,
  type NewPasswordValue,
} from '../auth/AuthFields';

type State =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'ready'; preview: PasswordResetPreview }
  | { kind: 'done' };

export function PasswordResetRoute(): ReactElement {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [value, setValue] = useState<NewPasswordValue>(EMPTY_NEW_PASSWORD);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    AuthApi.resetPreview(token).then(
      (preview) => {
        if (!cancelled) setState({ kind: 'ready', preview });
      },
      () => {
        if (!cancelled) setState({ kind: 'invalid' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(): Promise<void> {
    if (pending || !isNewPasswordValid(value)) return;
    setPending(true);
    setError(null);
    try {
      await AuthApi.resetAccept(token, {
        password: value.password,
        passwordConfirm: value.confirm,
      });
      setValue(EMPTY_NEW_PASSWORD);
      setState({ kind: 'done' });
    } catch (err) {
      const e = asApiError(err);
      if (e.code === 'invalid_or_expired_reset') {
        setValue(EMPTY_NEW_PASSWORD);
        setState({ kind: 'invalid' });
        return;
      }
      setError(authErrorMessage(t, e));
    } finally {
      setPending(false);
    }
  }

  if (state.kind === 'loading') {
    return (
      <main className="p-8" data-testid="password-reset-loading">
        <p className="text-ink-2">{t('password_reset.loading')}</p>
      </main>
    );
  }

  if (state.kind === 'invalid' || state.kind === 'done') {
    const done = state.kind === 'done';
    return (
      <main
        className="mx-auto max-w-xl space-y-3 p-8"
        data-testid={done ? 'password-reset-done' : 'password-reset-invalid'}
      >
        <h1 className="font-display text-display-s text-ink">
          {t(done ? 'password_reset.done_title' : 'password_reset.invalid_title')}
        </h1>
        <p className="text-ink-2 text-sm">
          {t(done ? 'password_reset.done_body' : 'password_reset.invalid_body')}
        </p>
        <Link
          className="text-primary text-sm underline"
          to="/login"
          data-testid="password-reset-to-login"
        >
          {t('password_reset.to_login')}
        </Link>
      </main>
    );
  }

  const { preview } = state;
  return (
    <main className="mx-auto max-w-xl space-y-5 p-6" data-testid="password-reset-route">
      <header className="space-y-2">
        <h1 className="font-display text-display-s text-ink">{t('password_reset.title')}</h1>
        <p className="text-ink-2 text-sm">{t('password_reset.body', { name: preview.memberName })}</p>
        {preview.username ? (
          <p className="text-ink-2 text-sm" data-testid="password-reset-username">
            {t('password_reset.username', { username: preview.username })}
          </p>
        ) : null}
        <p className="text-ink-2 text-xs">
          {t('password_reset.expires', {
            at: new Date(preview.expiresAt).toLocaleString('nb-NO'),
          })}
        </p>
      </header>
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <HiddenUsername value={preview.username} />
        <NewPasswordFields value={value} onChange={setValue} testId="password-reset-password" />
        {error ? (
          <p className="text-bad text-sm" role="alert" data-testid="password-reset-error">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={pending || !isNewPasswordValid(value)}
            data-testid="password-reset-submit"
          >
            {pending ? t('account.saving') : t('password_reset.submit')}
          </Button>
        </div>
      </form>
    </main>
  );
}
